import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import ProductFAQ from '@/components/landing/ProductFAQ';
import React, { Suspense } from 'react';
import Header from '@/components/landing/product/sub/Header'
import databg from '@/assets/products/forex.png';

import { Database, Zap, Globe, Clock, TrendingDown, Activity } from 'lucide-react';
import StyledProducts from '../styles/ProductsPageStyles';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Marquee from '@/components/landing/product/sub/Logos';
import FeaturesGrid from '@/components/landing/product/sub/FeaturesGrid';

const loadGlobeComp = () => import('@/components/landing/product/sub/Globe');
const GlobeComp = React.lazy(loadGlobeComp);
const loadDataGrid = () => import('@/components/landing/product/DataGrid');
const DataGrid = React.lazy(loadDataGrid);
const loadCardList = () => import('@/components/landing/product/CardList');
const CardList = React.lazy(loadCardList);
const loadResponses = () => import('@/components/landing/product/sub/Responses');
const Responses = React.lazy(loadResponses);
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails');
const DatasetDetails = React.lazy(loadDatasetDetails);
const loadW1 = () => import('@/components/landing/product/main/datasets/forex');
const W1 = React.lazy(loadW1);
const loadW2 = () => import('@/components/landing/product/main/datasets/forex-carry-card');
const W2 = React.lazy(loadW2);
const loadW3 = () => import('@/components/landing/product/main/datasets/forex-session-card');
const W3 = React.lazy(loadW3);
const loadW4 = () => import('@/components/landing/product/main/datasets/macro-regime-card');
const W4 = React.lazy(loadW4);
const loadW5 = () => import('@/components/landing/product/main/datasets/macro-nowcast-card');
const W5 = React.lazy(loadW5);
const loadOutcomes = () => import('@/components/landing/product/sub/OutcomeStats');
const Outcomes = React.lazy(loadOutcomes);
const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);


const StocksPage: React.FC = () => {
  useIdlePrefetch([loadGlobeComp, loadDataGrid, loadCardList, loadResponses, loadDatasetDetails, loadW1, loadW2, loadW3, loadW4, loadW5, loadOutcomes, loadPricingTable, loadGetStarted, loadFooter]);
  return (
    <>
      <SEOMetadata
        title="Forex Data API: Daily & Historical Exchange Rates"
        description="Forex data API with daily and historical exchange rates for 180+ currency pairs, decades of history, central bank policies, and volatility metrics."
        canonicalUrl="https://axionquant.com/products/forex"
        />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Axion Forex Data API",
          "description": "Forex data API with daily and historical exchange rates for 180+ currency pairs, decades of history back to 1987, central bank policies, and volatility metrics.",
          "brand": { "@type": "Brand", "name": "AxionQuant" },
          "image": "https://axionquant.com/meta.jpg",
          "category": "Forex Data API",
          "offers": { "@type": "AggregateOffer", "lowPrice": "29", "highPrice": "99", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Free tier available; paid plans from $29/month" }
        })}</script>
      </Helmet>

      <StyledProducts>


      <Header
  docs="/docs/01-market-data/forex"
  title="Forex Data API & Exchange Rates"
  body="Daily and hourly forex data across 180+ currency pairs, with decades of history back to 1987. Power systematic strategies, cross-rate analysis, and volatility research with clean, normalized currency exchange rate data."
  crumb={{name: 'Forex', path:'/products/forex'}}
  tabs={[
    {
      label: 'Spot Direction',
      language: 'python',
      filename: 'spot_direction.py',
      bg: '4.jpeg',
      code: `from axion import Axion, utils, visualize, ta

client = Axion(api_key="your_api_key")

df = utils.df(
    client.forex.prices(
        "EURUSD",
        from_date=utils.d(utils.yearago),
        frame="daily",
    )
)

df["ema_21"] = ta.ema(df, period=21)
df["ema_50"] = ta.ema(df, period=50)
df["rsi"]    = ta.rsi(df, period=14)

visualize.graph(
    df,
    x="date",
    lines=["close", "ema_21", "ema_50"],
    title="EUR/USD - Spot Direction",
)
`,
    },
    {
      label: 'Multi-Pair Momentum',
      language: 'python',
      filename: 'multi_pair_momentum.py',
      bg: '2.jpeg',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

pairs   = ["EURUSD", "GBPUSD", "USDJPY", "AUDUSD", "USDCHF"]
from_dt = utils.d(utils.yearago)

prices = {
    p: utils.df(client.forex.prices(p, from_date=from_dt))
    for p in pairs
}

df = utils.snap(
    [prices[p] for p in pairs],
    names=pairs,
    overwrite=["close"],
    col="date",
)

df = utils.relativity(df, cols=pairs)

visualize.polls(df)
`,
    },
    {
      label: 'Volatility Regime',
      language: 'python',
      filename: 'volatility_regime.py',
      bg: '3.jpeg',
      code: `from axion import Axion, utils, visualize, ta

client = Axion(api_key="your_api_key")

pairs   = ["EURUSD", "GBPUSD", "USDJPY"]
from_dt = utils.d(utils.yearago)

def fetch_vol(row):
    p  = row["pair"]
    df = utils.df(client.forex.prices(p, from_date=from_dt))
    df["atr"] = ta.atr(df, period=14)
    return (p, {
        "pair":    p,
        "avg_atr": df["atr"].mean(),
        "max_atr": df["atr"].max(),
    })

df_pairs  = utils.df([{"pair": p} for p in pairs])
vol_map   = {}
utils.work(df_pairs, fetch_vol, vol_map)

df_vol = utils.df(list(vol_map.values()))

visualize.bar(df_vol, x="pair", y="avg_atr")
`,
    },
    {
      label: 'Cross-Rate Spread',
      language: 'python',
      filename: 'cross_rate_spread.py',
      bg: '1.jpeg',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

pairs   = [("EURUSD", "GBPUSD"), ("AUDUSD", "NZDUSD")]
from_dt = utils.d(utils.yearago)

for leg_a, leg_b in pairs:
    df_a = utils.df(client.forex.prices(leg_a, from_date=from_dt))
    df_b = utils.df(client.forex.prices(leg_b, from_date=from_dt))

    visualize.spread(
        [df_a, df_b],
        x="date",
        y="close",
    )
`,
    },
  ]}
/>

      <Marquee/>

      <FeaturesGrid
        title="Forex Data API & Intelligence. "
        subtitle="Exchange rates, deep history, and analytics. "
        features={ [
        {
          fig: 'FIG 0.1',
          icon: <Zap size={80} color="var(--purple)" />,
          title: 'Daily & Hourly FX Data',
          description: 'Daily and hourly exchange rates across 300+ currency pairs. Pull decades of history back to 1987 nearly instantly for backtesting and long-horizon research.',
        },
        {
          fig: 'FIG 0.2',
          icon: <Clock size={80} color="var(--green)" />,
          title: 'Historical Exchange Rates',
          description: '35+ years of clean, historical FX data, spot rates, and forward curves. Daily OHLCV bars for backtesting and quantitative research.',
        },
        {
          fig: 'FIG 0.3',
          icon: <Database size={80} color="var(--red)" />,
          title: 'Central Bank Policies',
          description: 'Interest rate decisions, meeting minutes sentiment, balance sheet changes, and economic indicator surprises. Model fundamental policy divergence.',
        },
        {
          fig: 'FIG 0.4',
          icon: <TrendingDown size={80} color="var(--purple)" />,
          title: 'Volatility & Risk Reversals',
          description: 'Implied volatility surfaces, risk reversals, and skew across tenors. Gauge market positioning, hedging demand, and tail risk perceptions.',
        },
        {
          fig: 'FIG 0.5',
          icon: <Activity size={80} color="var(--green)" />,
          title: 'Cross Rates & Triangulation',
          description: 'Automatically calculated cross rates for all currency pairs. Precise triangulation logic ensures consistency across the entire FX universe.',
        },
        {
          fig: 'FIG 0.6',
          icon: <Globe size={80} color="var(--red)" />,
          title: 'Global FX Coverage',
          description: 'Full spectrum of major, minor, and exotic currency pairs with currency pair data and exchange rate data. Spot, forward, and options pricing from one API with institutional-grade reliability.',
        },
      ]}/>



      <Suspense fallback={null}>
      <GlobeComp
        eyebrow="Forex Data Platform"
        headingPlain="Forex data"
        headingEm="across every currency pair."
        body="Institutional-grade forex data API coverage across spot, forward, and options markets - normalized, timestamped, and ready for production."
        stats={[
          { v: "300+", l: "Currency Pairs" },
          { v: "50+",  l: "Central Banks" },
          { v: "35+yr", l: "History" },
          { v: "99.95%", l: "Uptime SLA" },
        ]}
        markers={[
  { id: "usd", location: [38.89, -77.03], label: "USD" },
  { id: "eur", location: [50.11, 8.68], label: "EUR" },
  { id: "jpy", location: [35.68, 139.77], label: "JPY" },
  { id: "gbp", location: [51.51, -0.09], label: "GBP" },
  { id: "aud", location: [-33.87, 151.21], label: "AUD" },
  { id: "cad", location: [45.42, -75.70], label: "CAD" },
  { id: "chf", location: [46.95, 7.45], label: "CHF" },
  { id: "cny", location: [39.90, 116.40], label: "CNY" },
  { id: "nzd", location: [-41.29, 174.78], label: "NZD" },
  { id: "sek", location: [59.33, 18.07], label: "SEK" },
  { id: "nok", location: [59.91, 10.75], label: "NOK" },
  { id: "dkk", location: [55.68, 12.57], label: "DKK" },
  { id: "sgd", location: [1.30, 103.80], label: "SGD" },
  { id: "hkd", location: [22.28, 114.16], label: "HKD" },
  { id: "krw", location: [37.57, 126.98], label: "KRW" },
  { id: "inr", location: [19.08, 72.88], label: "INR" },
  { id: "brl", location: [-15.80, -47.86], label: "BRL" },
  { id: "zar", location: [-25.75, 28.19], label: "ZAR" },
  { id: "try", location: [39.93, 32.85], label: "TRY" },
  { id: "rub", location: [55.75, 37.62], label: "RUB" },
  { id: "mxn", location: [19.43, -99.13], label: "MXN" },
  { id: "idr", location: [-6.21, 106.85], label: "IDR" },
  { id: "thb", location: [13.73, 100.50], label: "THB" },
  { id: "myr", location: [3.14, 101.69], label: "MYR" },
  { id: "php", location: [14.59, 120.98], label: "PHP" },
  { id: "vnd", location: [21.03, 105.85], label: "VND" },
  { id: "twd", location: [25.03, 121.52], label: "TWD" },
  { id: "ils", location: [31.77, 35.21], label: "ILS" },
  { id: "aed", location: [24.45, 54.40], label: "AED" },
  { id: "sar", location: [24.71, 46.67], label: "SAR" },
  { id: "qar", location: [25.29, 51.53], label: "QAR" },
  { id: "kwd", location: [29.38, 47.98], label: "KWD" },
  { id: "bhd", location: [26.23, 50.58], label: "BHD" },
  { id: "omr", location: [23.59, 58.41], label: "OMR" },
  { id: "jod", location: [31.95, 35.93], label: "JOD" },
  { id: "egp", location: [30.04, 31.24], label: "EGP" },
  { id: "ngn", location: [9.08, 7.54], label: "NGN" },
  { id: "kes", location: [-1.29, 36.82], label: "KES" },
  { id: "ghs", location: [5.56, -0.20], label: "GHS" },
  { id: "mad", location: [34.02, -6.84], label: "MAD" },
  { id: "ars", location: [-34.60, -58.38], label: "ARS" },
  { id: "clp", location: [-33.45, -70.65], label: "CLP" },
  { id: "cop", location: [4.71, -74.07], label: "COP" },
  { id: "pen", location: [-12.05, -77.04], label: "PEN" },
  { id: "uyu", location: [-34.89, -56.16], label: "UYU" },
  { id: "pyg", location: [-25.30, -57.64], label: "PYG" },
  { id: "bob", location: [-16.50, -68.15], label: "BOB" },
  { id: "crc", location: [9.93, -84.09], label: "CRC" },
  { id: "dop", location: [18.49, -69.91], label: "DOP" },
  { id: "hnl", location: [14.07, -87.19], label: "HNL" },
  { id: "nio", location: [12.11, -86.24], label: "NIO" },
  { id: "pab", location: [8.98, -79.52], label: "PAB" },
  { id: "vef", location: [10.50, -66.92], label: "VEF" },
  { id: "czk", location: [50.09, 14.43], label: "CZK" },
  { id: "huf", location: [47.50, 19.05], label: "HUF" },
  { id: "pln", location: [52.23, 21.01], label: "PLN" },
  { id: "ron", location: [44.43, 26.10], label: "RON" },
  { id: "bgn", location: [42.70, 23.32], label: "BGN" },
  { id: "hrk", location: [45.81, 15.98], label: "HRK" },
  { id: "isk", location: [64.15, -21.94], label: "ISK" },
  { id: "try", location: [39.93, 32.85], label: "TRY" },
  { id: "uah", location: [50.45, 30.52], label: "UAH" }
]}
      />

      <DataGrid
        title="Forex Data API: Beyond the Currency Pair"
        subtitle="Navigate the $7 trillion daily market with cross-asset context. Analyze daily and hourly forex data, spot, forward, and options pricing alongside the macro drivers, central bank signals, and capital flows that move exchange rates."
        bg={databg}
        stats={[
          { label: 'Currency Pairs', value: '300+' },
          { label: 'Central Banks Tracked', value: '50+' },
          { label: 'Forward Points (Tenors)', value: '12+' },
        ]}
      />
      <CardList
        title="Model the FX Market Drivers"
        vid={'https://cdn.studio14a.com/axion/products/cards/forex.mp4'}
        points={[
          {
            "title": "High-Fidelity Spot & Forward Curves",
            "details": "Access clean, historical daily and hourly forex data with spot rates alongside complete forward curves and swap points across major, minor, and exotic currency pairs.",
            "cta": "Explore forex pricing",
            "url": "/docs"
          },
          {
            "title": "Central Bank & Macro Policy Data",
            "details": "Track policy rates, meeting minutes sentiment, balance sheet changes, and economic indicator surprises. Model the fundamental policy divergence driving currency valuations.",
            "cta": "View macro catalysts",
            "url": "/products/economics"
          },
          {
            "title": "Options Volatility & Risk Reversals",
            "details": "Analyze implied volatility surfaces, risk reversals, and skew across tenors. Gauge market positioning, hedging demand, and tail risk perceptions.",
            "cta": "Analyze volatility data",
            "url": "/docs"
          }
        ]}
      />

      <Responses
        heading="Foreign Exchange Market Data"
        subheading="Daily and historical FX rates, currency pair metadata, and OHLCV aggregates for 180+ forex pairs"
        ticker="EURUSD"
        tabs={[
          {
            title: "All Forex Tickers",
            description: "Retrieves a comprehensive list of all available forex currency pairs with metadata",
            data:
            [
              {
                "id": 2,
                "ticker": "AEDBRX",
                "detail": "U.A.E. DIRHAM / BRAZILIAN REAL REFERENCE RATE",
                "exchange": "IDC",
                "country": "AE"
              },
              {
                "id": 3,
                "ticker": "AEDCAD",
                "detail": "U.A.E. DIRHAM / CANADIAN DOLLAR",
                "exchange": "IDC",
                "country": "AE"
              },
              {
                "id": 4,
                "ticker": "AEDCHF",
                "detail": "U.A.E. DIRHAM / SWISS FRANC",
                "exchange": "IDC",
                "country": "AE"
              },
            ]

          },
          {
            title: "Forex Ticker Details",
            description: "Retrieves detailed metadata for a specific forex currency pair",
            data: {
              "id": 907,
              "ticker": "EURUSD",
              "detail": "EURO / U.S. DOLLAR",
              "exchange": "IDC",
              "country": "EU",
              "rating": "strongsell",
              "lastClose": 1.1381744146,
              "changePct": -0.706805140494554
            }
          },
          {
            title: "Historical Exchange Rates",
            description: "Retrieves comprehensive historical exchange rate data with OHLCV",
            data: [
              {
                "time": "2026-06-23T00:00:00.000Z",
                "ticker": "EURUSD",
                "open": "1.1431183815",
                "high": "1.1441648006",
                "low": "1.1379153728",
                "close": "1.1381744146",
                "volume": "0"
              },
              {
                "time": "2026-06-21T00:00:00.000Z",
                "ticker": "EURUSD",
                "open": "1.1463682652",
                "high": "1.1473152637",
                "low": "1.1426090002",
                "close": "1.1462763548",
                "volume": "0"
              },
              {
                "time": "2026-06-18T00:00:00.000Z",
                "ticker": "EURUSD",
                "open": "1.1463946104",
                "high": "1.1475785971",
                "low": "1.1418262720",
                "close": "1.1458560228",
                "volume": "0"
              },
              {
                "time": "2026-06-17T00:00:00.000Z",
                "ticker": "EURUSD",
                "open": "1.1511453390",
                "high": "1.1530035734",
                "low": "1.1454228163",
                "close": "1.1507347822",
                "volume": "0"
              }]
          },

          {
            title: "Latest Quote",
            description: "Latest FX snapshot with latest OHLC, previous close, EMAs, 52-week range, and YTD change.",
            data: {
              "latestClose": 1.1381744146,
              "latestOpen": 1.1431183815,
              "latestHigh": 1.1441648006,
              "latestLow": 1.1379153728,
              "previousClose": 1.1462763548,
              "week52Range": {
                "high": 1.2024,
                "low": 1.1379
              },
              "week52AvgClose": 1.1671,
              "ema30": 1.158,
              "ema60": 1.162,
              "ema90": 1.1637,
              "ema120": 1.164,
              "ytdClosePctChange": -3.1379,
              "volume": 0,
              "avgVolumeLifetime": 0
            }
          },

          {
            title: "Sovereign Credit Ratings",
            description: "Credit ratings with issuer rating, outlook, review dates, and classification details.",
            data: [
              {
                "orgDebtTypeDesc": "Issuer Credit Rating",
                "rating": "AA+",
                "ratingDate": "20-May-2022",
                "currentCwOl": "Stable",
                "currentCwOlDate": "20-May-2022",
                "ratingDebtTypeCode": "ICR",
                "lastReviewDate": "29-May-2026",
                "coreOrgId": "100763",
                "endorsementIndicator": "EU|UKE",
                "coreRatingId": "932191675",
                "ratingTypeCode": "FCLONG",
                "ratingTypeCodeDesc": "Foreign Currency LT",
                "pcrFlag": true,
                "id": 1
              },
            ]
          },

          {
            title: "Momentum Flags",
            description: "FX pairs with largest percentage changes showing volatility or momentum shifts.",
            data: [
              {
                "ticker": "KRWGBP",
                "pctchange": 9680
              },
              {
                "ticker": "EURIRR",
                "pctchange": 4597.05047304
              },
              {
                "ticker": "GBPIRR",
                "pctchange": 4448.45016182042
              },
              {
                "ticker": "IDRCAD",
                "pctchange": -99.9912387030638
              },
              {
                "ticker": "IDRCHF",
                "pctchange": -99.2744020802456
              },
              {
                "ticker": "IDRGBP",
                "pctchange": -99.2309087332091
              },
              {
                "ticker": "IDRSGD",
                "pctchange": -99.2103316934239
              },
            ]
          },


        ]}
      />

      <DatasetDetails full={true}
        title="Comprehensive Forex Data Coverage"
        subtitle="Access daily and hourly exchange rates dating back to 1987, currency pair data, and cross-asset market data across major global exchanges. Power quantitative trading strategies, risk management, and portfolio analytics with institutional-grade data."
        cta="Get Started"
        ctaLink="/login"
        tiles={[
          {
            img: '1.webp',
            title: 'We cover 300+ currency pairs',
            caption: 'You can access forex data and cross-asset instruments across all major global exchanges.',
            cn: 'scaled',
            tile: <W1/>
          },
          {
            img: '9.webp',
            title: 'We connect to 85+ major exchanges',
            caption: 'You get comprehensive market data from trading venues around the world.',
            cn: 'scaled corner',
            tile: <W2/>
          },
          {
            img: '6.webp',
            title: 'We provide 400B+ data points',
            caption: 'You can perform deep quantitative analysis with vast historical daily and hourly datasets.',
            cn: 'scaled',
            tile: <W3/>
          },
          {
            img: '7.webp',
            title: 'We offer 35+ years of history',
            caption: 'You can backtest and validate trading strategies across decades of market data.',
            cn: 'scaled',
            tile: <W4/>
          },
          {
            img: '13.webp',
            title: 'We retrieve data in milliseconds',
            caption: 'You can pull decades of history nearly instantly to power long-horizon research and backtesting.',
            cn: 'scaled',
            tile: <W5/>
          }
        ]}
      />

      <Outcomes
        headline="Forex data API numbers that speak for themselves."
        leftCell={{ stat: '300+', title: '300+ Currency Pairs.', desc: 'Full coverage of major, minor, and exotic FX pairs with daily and hourly history.', showChart: true }}
        rightCells={[
          { stat: '50+', title: '50+ Central Banks.', desc: 'Interest rate decisions, policy statements, and economic calendar events.' },
          { stat: '12+', title: '12 Forward Tenors.', desc: 'Complete forward curves and swap points across standard maturities.', boldDesc: true },
        ]}
      /><ProductFAQ slug="forex" />


      <PricingTable />
      <GetStarted />

      <Footer />
      </Suspense>
      </StyledProducts>
    </>
  );
};

export default StocksPage;

